import React from 'react';
import {
  DashboardOutlined,
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { Avatar, Dropdown, Layout, Menu, Modal, Space } from 'antd';
import type { UnionExpand } from '@owl-lib/type-util';
import OwlLogo from '../../assets/logo-28.svg';
import { Locale, SessionContext } from '../../context/AppProvider';
import { FeatureKeys } from '../../data/session/interface';
import { actions as sessionSliceActions } from '../../data/session/logic';
import type { Params } from '../ClaimOverviewContainer/ClaimOverviewContainer';
import { ShellRouteDefs } from '../ClaimOverviewShellContainer/ClaimOverviewLandingContainer';
import { GeneralFeedbackComponent } from '../FeedbackContainer/FeedbackComponents';
import NotificationsContainer from '../NotificationsContainer/NotificationsContainer';
import OwlShellSiderComponent from '../OwlShell/components/OwlShellSiderComponent/OwlShellSiderComponent';
import shellStyles from '../OwlShell/OwlShellContainer.module.scss';
import { generatePath, RouteDefs } from '../Routing/Routing';
import styles from './MainContainer.module.scss';

// checks for "/claims/:dossierId"
const isInClaimOverviewPage = /^\/main\/claims\/[^/]+$/;
export const UserContextMenuComponent: React.FC<{
  direction?: 'horizontal' | 'vertical';
}> = ({ direction }) => {
  const { dossierId } = useParams() as UnionExpand<Params>;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const handleOk = () => {
    dispatch(sessionSliceActions.requestLogout());
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const session = React.useContext(SessionContext);
  const { messages } = React.useContext(Locale);
  const dropdownItems = React.useMemo(() => {
    return [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: (
          <Link
            data-testid="logout-btn"
            to="#"
            onClick={() => setIsModalOpen(true)}
          >
            {messages['platform.logout']}
          </Link>
        ),
      },
    ];
  }, [messages]);

  return (
    <Space
      direction={direction}
      className={clsx(direction === 'vertical' && styles.shellUserContextMenu)}
    >
      <GeneralFeedbackComponent dossier_id={dossierId} />
      <NotificationsContainer />
      <Avatar
        className={styles.statusAvatar}
        shape="circle"
        size={28}
        icon={<DashboardOutlined style={{ fontSize: '14px' }} />}
        onClick={() => navigate(generatePath(RouteDefs.Status))}
      />

      <Dropdown
        placement="topRight"
        menu={{ selectable: false, items: dropdownItems }}
        trigger={['click']}
      >
        <Avatar
          className={styles.avatar}
          icon={
            session?.current?.first_name ||
            session?.current?.last_name ? undefined : (
              <UserOutlined style={{ fontSize: '14px' }} />
            )
          }
          size={28}
        >
          <div style={{ fontSize: '8px', fontWeight: 700 }}>
            {session?.current
              ? `${session.current.first_name?.[0] ?? ''}${
                  session.current.last_name?.[0] ?? ''
                }`.toUpperCase()
              : null}
          </div>
        </Avatar>
      </Dropdown>
      <Modal open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
        <p>{messages['platform.logout_description']}</p>
      </Modal>
    </Space>
  );
};

const MainContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { messages } = React.useContext(Locale);
  const { current, features } = React.useContext(SessionContext);

  const menuItems = React.useMemo(() => {
    const results = [
      {
        key: 'claims',
        icon: <HomeOutlined />,
        label: (
          <Link to={generatePath(RouteDefs.Claims)}>
            {features[FeatureKeys.CustomerTerminology]
              ? messages['claims.title']
              : messages['claims.cases']}
          </Link>
        ),
      },
    ];

    if (current?.has_manager_role) {
      results.push({
        key: 'admin',
        icon: <UserSwitchOutlined />,
        label: (
          <Link to={generatePath(RouteDefs.AdminUsers)}>
            {messages['admin.title']}
          </Link>
        ),
      });
    }

    return results;
  }, [current?.has_manager_role, features, messages]);

  React.useEffect(() => {
    if (features[FeatureKeys.OwlShell]) {
      navigate(generatePath(ShellRouteDefs.OwlShell));
    }
  }, [features, navigate]);

  if (features[FeatureKeys.ShellSidebar]) {
    return (
      <Layout className={shellStyles.layout}>
        <OwlShellSiderComponent>
          <div className={styles.shellSiderChildren}>
            <Menu
              items={menuItems}
              selectedKeys={[location.pathname.split('/')[2]]}
            />
            <UserContextMenuComponent direction="vertical" />
          </div>
        </OwlShellSiderComponent>
        <Layout className={shellStyles.contentContainer}>
          <Layout className={shellStyles.content}>
            <Outlet />
          </Layout>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout className={styles.layout}>
      {
        // TODO: header is going away soon in place of global nav
        !isInClaimOverviewPage.test(location.pathname) && (
          <Layout.Header className={styles.header}>
            <OwlLogo />
            {features[FeatureKeys.AdminPage] && (
              <Menu
                className={styles.menu}
                selectedKeys={[location.pathname.split('/')[2]]}
                mode="horizontal"
                items={menuItems}
                selectable
              />
            )}
            <UserContextMenuComponent />
          </Layout.Header>
        )
      }
      <Layout className={styles.content}>
        <Outlet />
      </Layout>
    </Layout>
  );
};

export default MainContainer;
